import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  LinearProgress,
} from "@mui/material";
import { UploadFile, Lock, LockOpen } from "@mui/icons-material";
import Header from "../../components/Header";
import {
  useGetReviewQuery,
  useGetMembersQuery,
  useGetImportsQuery,
  useGetProgressQuery,
  useGetDashboardQuery,
  useSearchReviewersQuery,
  useUpdateReviewSettingsMutation,
  useToggleBlindingMutation,
  useArchiveReviewMutation,
  useAddImportMutation,
  useInviteMemberMutation,
  useSetMemberRoleMutation,
  useRemoveMemberMutation,
  useSetStageLockMutation,
} from "../../services/review.service";

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box mt={2}>{children}</Box>;
}

const SOURCE_TYPES = [
  { value: "database", label: "Database / register" },
  { value: "other", label: "Other methods" },
];

const STAGE_LABELS = { 1: "Titles", 2: "Titles and abstracts", 3: "Full text" };

const ScreeningPanel = ({ reviewId, review, isOwner, isOwnerOrArbiter }) => {
  const navigate = useNavigate();
  const { data: progressData, isLoading } = useGetProgressQuery(reviewId);
  const [setStageLock] = useSetStageLockMutation();

  if (isLoading || !progressData) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
        {isOwnerOrArbiter && (
          <Button variant="outlined" onClick={() => navigate(`/reviews/${reviewId}/conflicts`)}>
            View conflicts
          </Button>
        )}
        <Button variant="outlined" onClick={() => navigate(`/reviews/${reviewId}/my-decisions`)}>
          My decisions
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/reviews/${reviewId}/exports`)}>
          Exports &amp; PRISMA
        </Button>
      </Box>
      {[1, 2, 3].map((stage) => {
        const progress = progressData[`stage${stage}`];
        const locked = Boolean(review.stageLocks?.[`stage${stage}`]);
        const pct = progress.total > 0 ? (progress.done / progress.total) * 100 : 0;
        return (
          <Paper key={stage} sx={{ p: 2, mb: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              <Typography variant="subtitle1">
                Stage {stage}: {STAGE_LABELS[stage]}
              </Typography>
              {locked && <Chip icon={<Lock fontSize="small" />} label="Locked" size="small" />}
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Your progress: {progress.done} / {progress.total}
            </Typography>
            <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 4, mb: 2 }} />
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                disabled={progress.total === 0 || progress.done >= progress.total}
                onClick={() => navigate(`/reviews/${reviewId}/screen/${stage}`)}
              >
                {progress.done > 0 ? "Continue screening" : "Start screening"}
              </Button>
              {isOwner && (
                <Button
                  variant="outlined"
                  startIcon={locked ? <LockOpen /> : <Lock />}
                  onClick={() => setStageLock({ reviewId, stage, locked: !locked })}
                >
                  {locked ? "Unlock stage" : "Lock stage"}
                </Button>
              )}
            </Box>
          </Paper>
        );
      })}
      <Alert severity="info">Bibliography, exclusion and decision-log exports, and the PRISMA diagram arrive in a later phase.</Alert>
    </Box>
  );
};

function KappaLabel({ kappa }) {
  if (kappa === null || kappa === undefined) return <Typography variant="body2" color="text.secondary">n/a</Typography>;
  return <Typography variant="body2">{kappa.toFixed(2)}</Typography>;
}

const DashboardPanel = ({ reviewId }) => {
  const { data, isLoading } = useGetDashboardQuery(reviewId);
  if (isLoading || !data) return <Typography>Loading...</Typography>;

  return (
    <Box>
      {[1, 2, 3].map((stage) => {
        const s = data.stages[stage];
        if (!s) return null;
        return (
          <Paper key={stage} sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle1" mb={1}>Stage {stage}: {STAGE_LABELS[stage]}</Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
              {Object.entries(s.counts).map(([key, value]) => (
                <Chip key={key} label={`${key}: ${value}`} size="small" />
              ))}
            </Box>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Your progress: {s.myProgress.done} / {s.myProgress.total}
            </Typography>
            {s.blinded ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                Other reviewers' progress and agreement are hidden while Stage {stage} blinding is on.
              </Alert>
            ) : (
              <>
                {s.reviewerProgress && (
                  <List dense>
                    {s.reviewerProgress.map((rp) => (
                      <ListItem key={rp.userId} disableGutters>
                        <ListItemText primary={`${rp.username} (${rp.role})`} secondary={`${rp.done} / ${rp.total}`} />
                      </ListItem>
                    ))}
                  </List>
                )}
                {s.kappa?.pairwise?.length > 0 && (
                  <Box mt={1}>
                    <Typography variant="body2" fontWeight={600} mb={0.5}>Agreement</Typography>
                    {s.kappa.pairwise.map((p, i) => (
                      <Box key={i} display="flex" gap={2} alignItems="center" mb={0.5}>
                        <Typography variant="body2" color="text.secondary">
                          {p.reviewerA.username} vs {p.reviewerB.username} (n={p.n}):
                        </Typography>
                        <Typography variant="body2">
                          {p.percentAgreement !== null ? `${Math.round(p.percentAgreement * 100)}% agreement` : "n/a"}
                        </Typography>
                        <Typography variant="body2">kappa=</Typography>
                        <KappaLabel kappa={p.kappa} />
                      </Box>
                    ))}
                    {s.kappa.fleiss && (
                      <Typography variant="body2">
                        Fleiss' kappa (n={s.kappa.fleiss.n}): <KappaLabel kappa={s.kappa.fleiss.kappa} />
                      </Typography>
                    )}
                  </Box>
                )}
              </>
            )}
          </Paper>
        );
      })}
      {data.conflicts && (
        <Alert severity={data.conflicts.open > 0 ? "warning" : "success"}>
          {data.conflicts.open} open conflict(s), {data.conflicts.resolved} resolved.
        </Alert>
      )}
    </Box>
  );
};

const MembersPanel = ({ reviewId, isOwner }) => {
  const { data, isLoading } = useGetMembersQuery(reviewId);
  const [query, setQuery] = useState("");
  const { data: searchData } = useSearchReviewersQuery(
    { reviewId, q: query },
    { skip: !isOwner || query.trim().length < 2 }
  );
  const [inviteMember] = useInviteMemberMutation();
  const [setMemberRole] = useSetMemberRoleMutation();
  const [removeMember] = useRemoveMemberMutation();
  const [message, setMessage] = useState("");

  const members = data?.members || [];

  const handleInvite = async (userId) => {
    try {
      await inviteMember({ reviewId, userId }).unwrap();
      setQuery("");
      setMessage("Invite sent.");
    } catch (err) {
      setMessage(err?.data?.message || "Could not invite that user.");
    }
  };

  return (
    <Box>
      {message && <Alert severity="info" onClose={() => setMessage("")} sx={{ mb: 2 }}>{message}</Alert>}
      {isOwner && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" mb={1}>Invite a reviewer</Typography>
          <TextField
            label="Search by name or email"
            fullWidth
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <List dense>
            {(searchData?.users || []).map((user) => (
              <ListItem
                key={user._id}
                secondaryAction={
                  <Button size="small" variant="outlined" onClick={() => handleInvite(user._id)}>
                    Invite
                  </Button>
                }
              >
                <ListItemText primary={user.username} secondary={user.email} />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" mb={1}>Members</Typography>
        {isLoading && <Typography>Loading...</Typography>}
        <List>
          {members.map((member) => (
            <ListItem
              key={member._id}
              secondaryAction={
                isOwner && member.role !== "owner" ? (
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      onClick={() => setMemberRole({ reviewId, memberId: member._id, role: member.role === "arbiter" ? "reviewer" : "arbiter" })}
                    >
                      {member.role === "arbiter" ? "Unset arbiter" : "Make arbiter"}
                    </Button>
                    <Button size="small" color="error" onClick={() => removeMember({ reviewId, memberId: member._id })}>
                      Remove
                    </Button>
                  </Box>
                ) : null
              }
            >
              <ListItemText
                primary={member.user?.username || member.user?.email}
                secondary={member.user?.email}
              />
              <Chip label={member.role} size="small" sx={{ mr: 1 }} />
              <Chip
                label={member.status}
                size="small"
                color={member.status === "active" ? "success" : "default"}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

const ImportsPanel = ({ reviewId, isOwner }) => {
  const { data, isLoading } = useGetImportsQuery(reviewId);
  const [addImport, { isLoading: isAdding }] = useAddImportMutation();
  const [sourceLabel, setSourceLabel] = useState("");
  const [sourceType, setSourceType] = useState("database");
  const [files, setFiles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");

  const imports = data?.imports || [];

  const handleSubmit = async () => {
    setError("");
    setSummary(null);
    if (!sourceLabel || files.length === 0) {
      setError("A source label and at least one file are required.");
      return;
    }
    const formData = new FormData();
    formData.append("sourceLabel", sourceLabel);
    formData.append("sourceType", sourceType);
    files.forEach((file) => formData.append("files", file));
    try {
      const result = await addImport({ reviewId, formData }).unwrap();
      setSummary(result.imports);
      setFiles([]);
      setSourceLabel("");
    } catch (err) {
      setError(err?.data?.message || "Could not add the import.");
    }
  };

  return (
    <Box>
      {isOwner && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" mb={1}>Add an import</Typography>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {summary && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Parsed {summary.reduce((s, i) => s + i.parsed, 0)}, new {summary.reduce((s, i) => s + i.newCount, 0)},
              {" "}duplicates removed {summary.reduce((s, i) => s + i.duplicateOfExistingCount + i.duplicateWithinFileCount, 0)},
              {" "}possible duplicates {summary.reduce((s, i) => s + i.possibleDuplicateCount, 0)}.
            </Alert>
          )}
          <TextField
            label="Source label"
            fullWidth
            value={sourceLabel}
            onChange={(e) => setSourceLabel(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            label="Source type"
            fullWidth
            value={sourceType}
            onChange={(e) => setSourceType(e.target.value)}
            sx={{ mb: 2 }}
          >
            {SOURCE_TYPES.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
          <Button variant="outlined" component="label" startIcon={<UploadFile />}>
            Choose files (.nbib, .ris, .csv)
            <input
              type="file"
              hidden
              multiple
              accept=".nbib,.ris,.csv"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
            />
          </Button>
          {files.length > 0 && (
            <Typography variant="body2" sx={{ mt: 1 }}>{files.map((f) => f.name).join(", ")}</Typography>
          )}
          <Box mt={2}>
            <Button variant="contained" disabled={isAdding} onClick={handleSubmit}>
              {isAdding ? "Importing..." : "Add import"}
            </Button>
          </Box>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" mb={1}>Imports</Typography>
        {isLoading && <Typography>Loading...</Typography>}
        <List>
          {imports.map((imp) => (
            <ListItem key={imp._id} divider>
              <ListItemText
                primary={`${imp.sourceLabel} (${imp.format}) — ${imp.filename}`}
                secondary={`Parsed ${imp.parsedCount}, new ${imp.newCount}, duplicates of existing ${imp.duplicateOfExistingCount}, duplicates within file ${imp.duplicateWithinFileCount}, possible duplicates ${imp.possibleDuplicateCount} — uploaded by ${imp.uploader?.username || "?"}`}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

const SettingsPanel = ({ review, isOwner }) => {
  const [updateSettings] = useUpdateReviewSettingsMutation();
  const [toggleBlinding] = useToggleBlindingMutation();
  const [archiveReview] = useArchiveReviewMutation();
  const [criteria, setCriteria] = useState(review.criteria);
  const [manualExclusionsCount, setManualExclusionsCount] = useState(review.manualExclusionsCount || 0);

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" mb={1}>Screening criteria</Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          value={criteria}
          onChange={(e) => setCriteria(e.target.value)}
          disabled={!isOwner}
        />
        {isOwner && (
          <Button sx={{ mt: 1 }} variant="outlined" onClick={() => updateSettings({ reviewId: review._id, criteria })}>
            Save
          </Button>
        )}
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" mb={1}>PRISMA: records removed for other reasons</Typography>
        <Typography variant="body2" color="text.secondary" mb={1}>
          A manual count for the identification box, for removals the system can't detect on its own.
        </Typography>
        <Box display="flex" gap={1} alignItems="center">
          <TextField
            type="number"
            size="small"
            value={manualExclusionsCount}
            onChange={(e) => setManualExclusionsCount(e.target.value)}
            disabled={!isOwner}
            sx={{ width: 120 }}
          />
          {isOwner && (
            <Button
              variant="outlined"
              onClick={() => updateSettings({ reviewId: review._id, manualExclusionsCount: Number(manualExclusionsCount) })}
            >
              Save
            </Button>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" mb={1}>Blinding</Typography>
        {[1, 2, 3].map((stage) => (
          <FormControlLabel
            key={stage}
            control={
              <Switch
                checked={Boolean(review.blinding?.[`stage${stage}`])}
                disabled={!isOwner}
                onChange={(e) => toggleBlinding({ reviewId: review._id, stage, value: e.target.checked })}
              />
            }
            label={`Stage ${stage} blinding`}
          />
        ))}
      </Paper>

      {isOwner && review.status !== "archived" && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" mb={1}>Archive</Typography>
          <Button color="error" variant="outlined" onClick={() => archiveReview(review._id)}>
            Archive this review
          </Button>
        </Paper>
      )}
      {review.status === "archived" && <Alert severity="warning" sx={{ mt: 2 }}>This review is archived.</Alert>}
    </Box>
  );
};

const ReviewDetail = () => {
  const { reviewId } = useParams();
  const { data, isLoading } = useGetReviewQuery(reviewId);
  const [tab, setTab] = useState(0);

  if (isLoading || !data) {
    return (
      <Box m="1.5rem 2.5rem">
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  const { review, membership, isGlobalAdmin } = data;
  const isOwner = isGlobalAdmin || membership?.role === "owner";
  const isOwnerOrArbiter = isOwner || membership?.role === "arbiter";

  return (
    <Box m="1.5rem 2.5rem">
      <Header title={review.title} subtitle={review.question} />
      <Divider sx={{ my: 2 }} />
      <Tabs value={tab} onChange={(e, v) => setTab(v)}>
        <Tab label="Screening" />
        <Tab label="Dashboard" />
        <Tab label="Settings" />
        <Tab label="Members" />
        <Tab label="Imports" />
      </Tabs>
      <TabPanel value={tab} index={0}>
        <ScreeningPanel reviewId={reviewId} review={review} isOwner={isOwner} isOwnerOrArbiter={isOwnerOrArbiter} />
      </TabPanel>
      <TabPanel value={tab} index={1}>
        <DashboardPanel reviewId={reviewId} />
      </TabPanel>
      <TabPanel value={tab} index={2}>
        <SettingsPanel review={review} isOwner={isOwner} />
      </TabPanel>
      <TabPanel value={tab} index={3}>
        <MembersPanel reviewId={reviewId} isOwner={isOwner} />
      </TabPanel>
      <TabPanel value={tab} index={4}>
        <ImportsPanel reviewId={reviewId} isOwner={isOwner} />
      </TabPanel>
    </Box>
  );
};

export default ReviewDetail;
